import * as Sentry from "@sentry/nextjs";
import jsPDF, { Context2d, GState } from "jspdf";
import Konva from "konva";
import { preparedAddingFonts } from "frontend/jspdf-fonts/add-fonts";
import { Image } from "konva/types/shapes/Image";
import { svgPathToDrawCmds } from "../geometry/svg-path-utils";
import { rectIntersectRect } from "../geometry/intersections";
import { Shape } from "konva/types/Shape";
import { getPathPrefix } from "../utils/getPathPrefix";
import { Color } from "./colors";
import { CanvasScratchpad } from "./canvas-scratchpad";

interface Point {
  x: number;
  y: number;
}

interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface RoundedCorner {
  start: Point;
  startAngle: number;
  end: Point;
  endAngle: number;
  center: Point;
  counterClockwise: boolean;
  radius: number;
}

// Taken from https://unicode.org/reports/tr51/#EBNF_and_Regex
// new RegExp("\\p{RGI_Emoji}", "gv")
let regex_emoji = new RegExp(
  "\\p{RI}\\p{RI}|\\p{Emoji}(\\p{EMod}|\\uFE0F\\u20E3?|[\\uE0020-\\uE007E]+\\uE007F)?(\\u200D(\\p{RI}\\p{RI}|\\p{Emoji}(\\p{EMod}|\\uFE0F\\u20E3?|[\\uE0020-\\uE007E]+\\uE007F)?))*",
  "gu"
);
// look here: https://en.wikipedia.org/wiki/Basic_Latin_(Unicode_block)#cite_note-12
// for explanation
const charsWithVariation = "0123456789#*";

export class PdfExporter {
  private doc: PdfDocument | null = null;
  private pages: Rect[] | undefined;
  private globalScaleFactor = 1;

  // Beware: function can throw exceptions!!
  static async create(): Promise<PdfExporter> {
    const exporter = new PdfExporter();
    const fontsRes = await fetch(getPathPrefix("/base64-fonts/canvas-fonts.json"));
    const fonts = await fontsRes.json();
    const callback = preparedAddingFonts(fonts);
    jsPDF.API.events.push(["addFonts", callback]);
    return exporter;
  }

  prepareDocument(pages: Rect[], props?: { title?: string; author?: string; creator?: string }, stageScale = 1) {
    // jspdf limits every page to 14400. If any page is larger,
    // I'll scale the whole document down.
    let maxSize = Number.MIN_SAFE_INTEGER;
    for (const page of pages) {
      maxSize = Math.max(maxSize, page.width, page.height);
    }
    if (maxSize >= 14400) {
      this.globalScaleFactor = 14400 / maxSize;
    }

    this.pages = pages;
    const first = pages[0];
    const doc = new jsPDF({
      unit: "px", // Resulting PDF document is 96PPI
      format: [first.width * this.globalScaleFactor, first.height * this.globalScaleFactor],
      orientation: first.width > first.height ? "l" : "p",
      hotfixes: ["px_scaling"],
      putOnlyUsedFonts: true,
    });
    if (props) doc.setDocumentProperties(props);
    for (let index = 1; index < pages.length; index++) {
      // add page with width of 1st page, keep aspect ratio
      // TODO: if first page is in landscape, should we go by its height ?
      const width = pages[0].width;
      const height = pages[index].height * (width / pages[index].width);
      doc.addPage([width * this.globalScaleFactor, height * this.globalScaleFactor], width > height ? "l" : "p");
    }
    this.doc = new PdfDocument(doc, stageScale);
  }

  page(layer: Konva.Layer | null, page: number, rect: Rect, frameId?: string) {
    if (!this.doc || !layer) {
      throw new Error("Document not prepared");
    }
    const scale = (this.globalScaleFactor * this.pages![0].width) / rect.width;
    this.doc.exportArea(layer, page + 1, rect, scale, frameId);
  }

  public save(filename?: string) {
    this.doc?.save(filename);
  }
}

export class PdfDocument {
  private ctx: Context2d;
  private gstate: GState;
  private curPageArea!: Rect;
  private curPageScale!: number;
  private fakeKonvaCtx: any;
  private scratchpad: CanvasScratchpad;

  constructor(private readonly doc: jsPDF, stageScale = 1) {
    this.ctx = this.doc.canvas.getContext("2d");
    this.ctx.textBaseline = "middle";
    this.ctx.autoPaging = false;
    this.gstate = this.doc.GState({ opacity: 1 });
    this.scratchpad = new CanvasScratchpad();
    this.fakeKonvaCtx = createFakeKonvaCtx(doc, () => this.curPageArea, this.scratchpad);
    this.fakeKonvaCtx.pageScale = stageScale;
  }

  public exportArea(layer: Konva.Layer, pageNumberOneBased: number, rect: Rect, scale: number, frameId?: string) {
    this.doc.setPage(pageNumberOneBased);
    const m = this.doc.Matrix(scale, 0, 0, scale, -rect.x * scale, -rect.y * scale);
    this.ctx.transform(m.a, m.b, m.c, m.d, m.e, m.f);

    this.curPageArea = rect;
    this.curPageScale = scale;
    this.doc.advancedAPI(); // jspdf is buggy. must switch to advanced api and back for every page (otherwise they are flipped)
    this.exportNode(layer, frameId);
    this.doc.compatAPI();

    const mm = m.inversed();
    this.ctx.transform(mm.a, mm.b, mm.c, mm.d, mm.e, mm.f);
  }

  private matrix(transform: Konva.Transform) {
    const m = transform.getMatrix();
    return this.doc.Matrix(m[0], m[1], m[2], m[3], m[4], m[5]);
  }

  private exportNode(node: Konva.Node, frameId?: string) {
    // when exporting frames, we don't draw the boundary and the frame title.
    // when exporting the entire canvas, we do
    if (frameId) {
      if ((node.attrs.isFrameTitle && !node.attrs.isTableTitle) || node.attrs.isFrame) return;
      if (node.attrs.element?.frameId && node.attrs.element.frameId !== frameId) return;
    }
    if (!node.visible()) return;
    if (!rectIntersectRect(node.getClientRect({ relativeTo: node.getLayer() as any }), this.curPageArea)) return;

    const m = this.matrix(node.getTransform());
    this.ctx.save();
    this.ctx.transform(m.a, m.b, m.c, m.d, m.e, m.f);

    try {
      switch (node.getClassName()) {
        case "Group":
        case "Layer": {
          break;
        }
        case "Text": {
          this.exportText(node as Konva.Text);
          break;
        }
        case "Image": {
          this.exportImage(node as Konva.Image);
          break;
        }
        default: {
          this.exportShape(node as Konva.Shape);
          break;
        }
      }
      if (node.hasChildren()) {
        node.getChildren().each((child) => this.exportNode(child, frameId));
      }
    } catch (error) {
      console.warn("Error exporting node", error);
      Sentry.captureException(error, { tags: { type: "pdf-export" } });
    } finally {
      this.ctx.restore();
      this.doc.setGState(this.gstate);
    }
  }

  exportImage(image: Image) {
    let img = image.image();
    if (!img) return;
    // img can be one of:
    // HTMLImageElement | SVGImageElement | HTMLVideoElement | HTMLCanvasElement | ImageBitmap | OffscreenCanvas | VideoFrame
    // We don't export videos or video frames
    if (img instanceof HTMLVideoElement || "duration" in img) {
      console.warn("can't export video elements");
      return;
    }
    const imageTransform = image.getAbsoluteTransform(image.getLayer() as any).decompose();

    // I'm using jspdf addImage instead of context.drawImage because drawImage has problems with negative rotation
    // angles. I believe addImage also has them, but there I can control the rotation angle myself and correct it.
    // addImage draws images rotated around their bottom-left corner, whereas konva rotates around the center.
    // I'm working in advanced-api mode, so jspdf gives me a coordinate system where y=0 is the top of the page,
    // much like canvas.

    //TODO: I don't support:
    // - arbitrary crop
    // - stroke around image
    // - shadows, blur, alpha, filters

    const rotation_in_0_360_range = (imageTransform.rotation + 360) % 360; // jspdf uses 0-360 range
    const widthOnCanvas = image.width() * imageTransform.scaleX;
    const heightOnCanvas = image.height() * imageTransform.scaleY;
    const r = new DOMMatrix().rotateSelf(rotation_in_0_360_range);
    const p = r.transformPoint({ x: 0, y: heightOnCanvas });

    // Copy the image to a temporary canvas, in the exact size it's presented on workcanvas
    // TODO: when we'll have global image cache we can use it here
    const canvas = document.createElement("canvas");
    canvas.width = widthOnCanvas;
    canvas.height = heightOnCanvas;
    const drawContext = canvas.getContext("2d")!;
    if (image.attrs.xclip === "circle") {
      drawContext.beginPath();
      drawContext.arc(canvas.width / 2, canvas.height / 2, canvas.width / 2, 0, 2 * Math.PI);
      drawContext.clip();
    }
    drawContext.drawImage(img, 0, 0, canvas.width, canvas.height);

    // Now we can finally draw the image to the pdf, adjusting its position and scale as needed
    this.doc.addImage(
      canvas,
      "PNG",
      (imageTransform.x - this.curPageArea.x + p.x) * this.curPageScale,
      (imageTransform.y - heightOnCanvas - this.curPageArea.y + p.y) * this.curPageScale,
      widthOnCanvas * this.curPageScale,
      heightOnCanvas * this.curPageScale,
      "",
      "MEDIUM",
      rotation_in_0_360_range
    );
  }

  private exportText(text: Konva.Text) {
    if (!text.text()) return;
    if (!text.fillEnabled()) return;

    const textColor = Color.create(text.fill());
    if (!textColor) return;

    const textArray = text.textArr,
      textArrayLength = textArray.length;

    // most code copy-pasted from Konva's text object, to maintain maximum similiarity in rendering
    // Some numbers are magic numbers chosen by trial and error, just because canvas rendering and pdf
    // rendering are different.
    const padding = text.padding(),
      fontSize = text.fontSize(),
      lineHeightPx = text.lineHeight() * fontSize,
      verticalAlign = text.verticalAlign(),
      align = text.align(),
      totalWidth = text.getWidth(),
      textDecoration = text.textDecoration(),
      shouldUnderline = textDecoration.includes("underline"),
      shouldLineThrough = textDecoration.includes("line-through");

    let translateY = lineHeightPx / 2 - lineHeightPx / 14; // middle of the line (-h/14 is magic)

    // bold 200px doesn't work; it's exported as 16px font size in the pdf
    // normal 300px doesn't work also - TEST AGAIN
    // the problem is when exporting frames, not whole canvas
    // I have no idea why...
    let fs = Math.min(Math.floor(fontSize), 9999);
    if (text.fontStyle() == "bold" && fs == 200) fs++;
    this.ctx.font = `${text.fontStyle()} ${fs}px / ${Math.floor(lineHeightPx)}px "${text.fontFamily()}"`;
    this.ctx.textAlign = "left";
    this.ctx.fillStyle = textColor.color;
    this.doc.setGState(this.doc.GState({ opacity: textColor.alpha }));

    // handle vertical alignment
    let alignY = 0;
    if (verticalAlign === "middle") {
      alignY = (text.getHeight() - textArrayLength * lineHeightPx - padding * 2) / 2;
    } else if (verticalAlign === "bottom") {
      alignY = text.getHeight() - textArrayLength * lineHeightPx - padding * 2;
    }
    this.ctx.translate(padding, alignY + padding);

    // Some objects we need for drawing emojis.
    // best to create them once here (they're constants for text element)
    const textTransform = text.getAbsoluteTransform(text.getLayer() as any).decompose();
    const angle = Angle.fromDegrees((textTransform.rotation + 360) % 360); // turn negative angles to 0..360 range
    const scaleAndRotate = new DOMMatrix()
      .scale(textTransform.scaleX * this.curPageScale, textTransform.scaleY * this.curPageScale)
      .rotate(angle.degrees);

    // draw text lines
    for (let n = 0; n < textArrayLength; n++) {
      let lineTranslateX = 0;
      const obj = textArray[n],
        textLine = obj.text,
        width = obj.width,
        lastLine = n == textArrayLength - 1; //obj.lastInParagraph,
      let spacesNumber, oneWord, lineWidth;

      // horizontal alignment
      this.ctx.save();
      if (align === "right") {
        lineTranslateX += totalWidth - width - padding * 2;
      } else if (align === "center") {
        lineTranslateX += (totalWidth - width - padding * 2) / 2;
      }

      if (shouldUnderline) {
        this.ctx.save();
        this.ctx.beginPath();

        const yOffset = Math.round(fontSize / 4);
        const x = lineTranslateX;
        const y = translateY + yOffset;
        this.ctx.moveTo(x, y);
        spacesNumber = textLine.split(" ").length - 1;
        oneWord = spacesNumber === 0;
        lineWidth = align === "justify" && !lastLine ? totalWidth - padding * 2 : width;
        this.ctx.lineTo(x + Math.round(lineWidth), y);

        // I have no idea what is real ratio
        // just /15 looks good enough
        this.ctx.lineWidth = fontSize / 15;

        // const gradient = this._getLinearGradient();
        this.ctx.strokeStyle = textColor.color;
        this.ctx.stroke();
        this.ctx.restore();
      }
      if (shouldLineThrough) {
        this.ctx.save();
        this.ctx.beginPath();
        const yOffset = -Math.round(fontSize / 4);
        this.ctx.moveTo(lineTranslateX, translateY + yOffset);
        spacesNumber = textLine.split(" ").length - 1;
        oneWord = spacesNumber === 0;
        lineWidth = align === "justify" && lastLine && !oneWord ? totalWidth - padding * 2 : width;
        this.ctx.lineTo(lineTranslateX + Math.round(lineWidth), translateY + yOffset);
        this.ctx.lineWidth = fontSize / 15;
        // const gradient = this._getLinearGradient();
        // this.ctx.strokeStyle = gradient || fill;
        this.ctx.strokeStyle = textColor.color;
        this.ctx.stroke();
        this.ctx.restore();
      }

      try {
        if (regex_emoji) {
          let cur = 0;
          let match;
          while ((match = regex_emoji.exec(textLine))) {
            // some characters (digits,#,*) are emojis since they can be presented
            // graphically if followed by Variation Selector property.
            // but mostly they're just text. so if we get them without variation-selector
            // we skip them.
            if (match[0].length == 1 && charsWithVariation.includes(match[0])) {
              continue;
            }

            // Calculate the start and end of the match
            const start = match.index;
            const end = start + match[0].length;
            // Write text from previous match end to current match start
            if (cur < start) {
              const subStringUptoEmoji = textLine.slice(cur, start);
              this.ctx.fillText(subStringUptoEmoji, lineTranslateX, translateY);
              lineTranslateX += this.scratchpad.measureOnCanvas(subStringUptoEmoji, this.ctx.font).width;
            }
            cur = end;

            // Draw the emoji as picture and copy to canvas
            try {
              const emoji = textLine.slice(start, end);

              const emojiImage = this.scratchpad.drawEmoji(emoji);
              const inverseAspectRatio = emojiImage.height / emojiImage.width;
              const dstWidth = fs * this.curPageScale * textTransform.scaleX;
              const dstHeight = fs * inverseAspectRatio * this.curPageScale * textTransform.scaleY;
              const width = this.scratchpad.measureOnCanvas(emoji, this.ctx.font, "middle").width;
              // p is now the center of where the emoji should be (in pdf-page coordinates)
              const p = scaleAndRotate.transformPoint({ x: lineTranslateX + width / 2, y: translateY });
              // compute pdf-page coordinats for addImage function so its center lands at p, rotated
              const l = addImageCentered(p.x, p.y, dstWidth, dstHeight, angle);
              this.doc.addImage(
                emojiImage.toDataURL(),
                "PNG",
                (textTransform.x - this.curPageArea.x) * this.curPageScale + l.x,
                (textTransform.y - this.curPageArea.y) * this.curPageScale + l.y,
                dstWidth,
                dstHeight,
                "",
                "MEDIUM",
                angle.degrees
              );
              lineTranslateX += width;
            } catch {
              console.warn("Error rendering emoji");
              this.ctx.fillText("X", lineTranslateX, translateY); // drawing X instead of emoji
              lineTranslateX += fs;
            }
          }

          // Finish the last part of the string
          this.ctx.fillText(textLine.slice(cur), lineTranslateX, translateY);
        } else {
          this.ctx.fillText(textLine, lineTranslateX, translateY);
        }
      } catch (err) {
        console.warn("Error rendering text", err);
      }
      this.ctx.restore();
      translateY += lineHeightPx;
    }
  }

  private exportShape(shape: Konva.Shape) {
    // We draw shapes that are transparent so Konva's transformer works on those, and
    // not on the graphical elements. Those should not be exported (they can in some cases
    // mess up the export)
    if (shape.fill() === "transparent" && shape.stroke() === "transparent") {
      return;
    }
    const c = this.fakeKonvaCtx;
    this.ctx.save();
    shape.sceneFunc()?.apply(shape, [c as any, shape]);
    this.ctx.restore();
  }

  save(filename?: string) {
    this.doc.save(filename);
    this.scratchpad.dispose();
  }
}

const fromTo = (p0: Point, p1: Point): Point => ({ x: p1.x - p0.x, y: p1.y - p0.y });
const len = (v: Point) => Math.sqrt(v.x * v.x + v.y * v.y);
const normalize = (v: Point): Point => {
  const length = len(v);
  return { x: v.x / length, y: v.y / length };
};
const avg = (p0: Point, p1: Point): Point => ({ x: (p0.x + p1.x) / 2, y: (p0.y + p1.y) / 2 });
const scale = (v: Point, s: number): Point => ({ x: v.x * s, y: v.y * s });
const vadd = (p0: Point, p1: Point): Point => ({ x: p0.x + p1.x, y: p0.y + p1.y });
const vangle = (v: Point) => Math.atan2(v.y, v.x);
const cross = (v0: Point, v1: Point) => v0.x * v1.y - v0.y * v1.x;

function arcTo(p1: Point, p2: Point, p3: Point, radius: number): RoundedCorner {
  const p2_p1 = normalize(fromTo(p2, p1)); // direction vector from p2 to p1
  const p2_p3 = normalize(fromTo(p2, p3)); // direction vector from p2 to p3
  const alpha = Math.acos(p2_p1.x * p2_p3.x + p2_p1.y * p2_p3.y); // acos(dot product of normalized vectors) = angle between vectors
  /*
can check that C is smaller than len(p1_p2) and len(p2_p3)
if not we decrease the radius:
min = Min(PP1, PP2) (for polygon is better to divide this value by 2)
segment > min ?
    segment = min
    radius = segment * |tan(angle / 2)|
*/
  const C = radius / Math.abs(Math.tan(alpha / 2));
  const bisector = normalize(avg(p2_p1, p2_p3));
  const center = vadd(p2, scale(bisector, radius / Math.sin(alpha / 2)));

  const c1 = vadd(p2, scale(p2_p1, C));
  const c2 = vadd(p2, scale(p2_p3, C));
  const startAngle = vangle(fromTo(center, c1));
  const endAngle = vangle(fromTo(center, c2));
  const counterClockwise = cross(p2_p1, p2_p3) > 0;

  return {
    start: c1,
    end: c2,
    center,
    startAngle,
    endAngle,
    radius,
    counterClockwise,
  };
}

function createFakeKonvaCtx(doc: jsPDF, getCurPageRef: () => Rect, pad: CanvasScratchpad) {
  const pdfCtx = doc.canvas.getContext();
  let fakeKonvaCtx: any;

  fakeKonvaCtx = {
    isPdfContext: true,
    subPathStart: { x: 0, y: 0 },
    lastpoint: { x: 0, y: 0 },

    newPath(d?: string) {
      if (d) {
        return d;
      }
      return this._context;
    },
    drawSvgPath(path: string, matrix?: DOMMatrixReadOnly) {
      //2d transform of point with given matrix
      // matrix has a transformPoint function, which allocates a new point
      const transform = (x: number, y: number) => {
        return matrix ? [matrix.a * x + matrix.c * y + matrix.e, matrix.b * x + matrix.d * y + matrix.f] : [x, y];
      };
      // loop on the draw commands in the path, transform the points and draw them
      // it's better to create an array of numbers, transform them all at once,
      // and take draw commands from a second array
      let x = 0,
        y = 0,
        cp1x = 0,
        cp1y = 0,
        cp2x = 0,
        cp2y = 0,
        firstX = 0,
        firstY = 0;
      for (const cmd of svgPathToDrawCmds(path)) {
        switch (cmd.cmd) {
          case "M": {
            [x, y] = transform(cmd.points[0], cmd.points[1]);
            firstX = x;
            firstY = y;
            this.moveTo(x, y);
            break;
          }
          case "L": {
            [x, y] = transform(cmd.points[0], cmd.points[1]);
            this.lineTo(x, y);
            break;
          }
          case "C": {
            [cp1x, cp1y] = transform(cmd.points[0], cmd.points[1]);
            [cp2x, cp2y] = transform(cmd.points[2], cmd.points[3]);
            [x, y] = transform(cmd.points[4], cmd.points[5]);
            this.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, x, y);
            break;
          }
          case "Q": {
            [cp1x, cp1y] = transform(cmd.points[0], cmd.points[1]);
            [x, y] = transform(cmd.points[2], cmd.points[3]);
            this.quadraticCurveTo(cp1x, cp1y, x, y);
            break;
          }
          case "A": {
            // A rx ry x-axis-rotation large-arc-flag sweep-flag x y
            const [rx, ry, xAxisRotation, largeArcFlag, sweepFlag, x1, y1] = cmd.points;
            // TODO: transform with the matrix
            svgArcToCanvasArc(x, y, rx, ry, xAxisRotation, largeArcFlag, sweepFlag, x1, y1, this);
            x = x1;
            y = y1;
            break;
          }
          case "Z": {
            this.lineTo(firstX, firstY);
            break;
          }
          // TODO: can support close-path,
          default:
        }
      }
    },

    fillStrokeShape(shape: Konva.Shape) {
      if (shape.attrs.fillAfterStrokeEnabled) {
        this.strokeShape(shape);
        this.fillShape(shape);
      } else {
        this.fillShape(shape);
        this.strokeShape(shape);
      }
    },

    strokeShape(shape: Konva.Shape) {
      if (shape.hasStroke()) {
        this._stroke(shape);
      }
    },
    _strokeLinearGradient(_shape: Konva.Shape) {
      console.error("PDF export doesn't support linear gradients yet");
      // const start = shape.getStrokeLinearGradientStartPoint(),
      //   end = shape.getStrokeLinearGradientEndPoint(),
      //   colorStops = shape.getStrokeLinearGradientColorStops(),
      //   grd = this.createLinearGradient(start.x, start.y, end.x, end.y);

      // if (colorStops) {
      //   // build color stops
      //   for (var n = 0; n < colorStops.length; n += 2) {
      //     grd.addColorStop(colorStops[n] as number, colorStops[n + 1] as string);
      //   }
      //   this.setAttr("strokeStyle", grd);
      // }
    },
    _stroke(shape: Konva.Shape) {
      if (shape.hasStroke()) {
        const dash = shape.dash();

        this._applyLineCap(shape);
        if (dash && shape.dashEnabled()) {
          this.setLineDash(dash);
          this.lineDashOffset = shape.dashOffset();
        }
        pdfCtx.lineWidth = shape.strokeWidth();

        // if (!shape.getShadowForStrokeEnabled()) {
        // this.setAttr('shadowColor', 'rgba(0,0,0,0)');
        // }

        // TODO: check shape.stroke() is string. It can be gradient and pattern
        this.strokeStyle = shape.stroke();
        shape._strokeFunc(this);
      }
    },

    fillShape(shape: Konva.Shape) {
      if (shape.fillEnabled()) {
        this._fill(shape);
      }
    },

    _fillColor(shape: Konva.Shape) {
      this.fillStyle = shape.fill();
      shape._fillFunc(this);
    },

    _fillPattern(_shape: Konva.Shape) {
      console.error("TBD");
      // this.setAttr('fillStyle', shape._getFillPattern());
      // shape._fillFunc(this);
    },

    // TODO: this is not working correctly. It's filling the entire page,
    _fillLinearGradient(shape: Konva.Shape) {
      const start = shape.fillLinearGradientStartPoint(),
        end = shape.fillLinearGradientEndPoint(),
        colorStops = shape.fillLinearGradientColorStops();
      const gradient: any = [];
      for (let i = 0; i < colorStops.length; i += 2) {
        gradient.push({
          offset: colorStops[i],
          color: Color.create(colorStops[i + 1] as string)!.asRGB(),
        });
      }
      const shading = doc.ShadingPattern(
        "axial",
        [start.x, start.y, end.x, end.y],
        gradient
        // gState?: GState,
        // matrix?: Matrix
      );
      const randomKey = "random-key" + Math.random().toString();
      doc.addShadingPattern(randomKey, shading).fill({
        key: randomKey,
        matrix: doc.unitMatrix, // this paints over the whole page!
      });
    },

    _fillRadialGradient(shape: Konva.Shape) {
      const r1 = shape.fillRadialGradientStartRadius(),
        r2 = shape.fillRadialGradientEndRadius(),
        { x: x1, y: y1 } = shape.fillRadialGradientStartPoint(),
        { x: x2, y: y2 } = shape.fillRadialGradientEndPoint(),
        colorStops = shape.fillRadialGradientColorStops();
      const gradient: any = [];
      for (let i = 0; i < colorStops.length; i += 2) {
        gradient.push({
          offset: colorStops[i],
          color: Color.create(colorStops[i + 1] as string)!.asRGB(),
        });
      }

      console.error("TBD");
      doc.ShadingPattern("radial", [x1, y1, r1, x2, y2, r2], gradient);
    },

    _fill(shape: Shape) {
      const hasColor = shape.fill(),
        fillPriority = shape.fillPriority();

      // priority fills
      if (hasColor && fillPriority === "color") {
        this._fillColor(shape);
        return;
      }

      const hasPattern = shape.fillPatternImage();
      if (hasPattern && fillPriority === "pattern") {
        this._fillPattern(shape);
        return;
      }

      const hasLinearGradient = shape.fillLinearGradientColorStops();
      if (hasLinearGradient && fillPriority === "linear-gradient") {
        this._fillLinearGradient(shape);
        return;
      }

      const hasRadialGradient = shape.fillRadialGradientColorStops();
      if (hasRadialGradient && fillPriority === "radial-gradient") {
        this._fillRadialGradient(shape);
        return;
      }

      // now just try and fill with whatever is available
      if (hasColor) {
        this._fillColor(shape);
      } else if (hasPattern) {
        this._fillPattern(shape);
      } else if (hasLinearGradient) {
        this._fillLinearGradient(shape);
      } else if (hasRadialGradient) {
        this._fillRadialGradient(shape);
      }
    },

    reset() {
      //TODO reset the transform
    },
    getCanvas() {
      throw new Error("no canvas in pdf context");
    },
    clear() {
      throw new Error("no canvas in pdf context");
    },
    _applyLineCap(shape: Shape) {
      const lineCap = shape.attrs.lineCap;
      if (lineCap) {
        pdfCtx.lineCap = lineCap;
      }
    },
    _applyOpacity(shape: Shape) {
      const absOpacity = shape.getAbsoluteOpacity();
      if (absOpacity !== 1) {
        pdfCtx.globalAlpha = absOpacity;
      }
    },
    _applyLineJoin(shape: Shape) {
      const lineJoin = shape.attrs.lineJoin;
      if (lineJoin) {
        pdfCtx.lineJoin = lineJoin;
      }
    },

    clearRect(x: number, y: number, width: number, height: number) {
      pdfCtx.clearRect(x, y, width, height);
    },
    // clip(fillRule?: CanvasFillRule): void;
    // clip(path: Path2D, fillRule?: CanvasFillRule): void;
    clip(..._args: any[]) {
      pdfCtx.clip();
    },
    cliphole: (r: Rect, element: { x: number; y: number; scaleX?: number; scaleY?: number; rotate?: number }) => {
      const { x, y, scaleX = 1, scaleY = 1, rotate = 0 } = element;
      const cx = r.x + r.width / 2;
      const cy = r.y + r.height / 2;
      const { width, height } = r;
      const halfw = width / 2,
        halfh = height / 2;
      // let's use DOMMatrix for the heavy lifting of the transformation
      const p = new DOMMatrix().translate(x, y).rotate(rotate).scale(scaleX, scaleY).transformPoint({ x: cx, y: cy });
      const page = getCurPageRef();
      pdfCtx.beginPath();
      doc
        .rect(0, 0, page.width, page.height, null)
        .rect(p.x - halfw * scaleX - page.x, p.y - halfh * scaleY - page.y, width * scaleX, height * scaleY, null)
        .clipEvenOdd();
      doc.discardPath();
    },

    createLinearGradient(_x0: number, _y0: number, _x1: number, _y1: number) {
      throw new Error("not implemented");
    },
    createPattern(_image: CanvasImageSource, _repetition: string | null) {
      throw new Error("not implemented");
    },
    createRadialGradient(_x0: number, _y0: number, _r0: number, _x1: number, _y1: number, _r1: number) {
      throw new Error("not implemented");
    },

    // fill(fillRule?: CanvasFillRule): void;
    // fill(path: Path2D, fillRule?: CanvasFillRule): void;
    fill() {
      pdfCtx.fill();
    },
    fillRect(x: number, y: number, width: number, height: number) {
      pdfCtx.fillRect(x, y, width, height);
    },
    fillText(text: string, x: number, y: number) {
      pdfCtx.fillText(text, x, y);
    },
    measureText(text: string) {
      return pad.measureOnCanvas(text, pdfCtx.font);
    },

    //--------------------------------

    beginPath() {
      pdfCtx.beginPath();
    },

    closePath() {
      pdfCtx.closePath();
      this.lastpoint = this.subPathStart;
    },

    moveTo(x: number, y: number) {
      pdfCtx.moveTo(x, y);
      this.subPathStart = this.lastpoint = { x, y };
    },

    arc(x: number, y: number, radius: number, startAngle: number, endAngle: number, counterClockwise?: boolean) {
      if (radius === 0) {
        this.lastpoint = {
          x,
          y,
        };
        return;
      }
      pdfCtx.arc(x, y, radius, startAngle, endAngle, !!counterClockwise);
      this.lastpoint = {
        x: x + Math.cos(endAngle) * radius,
        y: y + Math.sin(endAngle) * radius,
      };
    },

    arcTo(x1: number, y1: number, x2: number, y2: number, radius: number) {
      const plan = arcTo(this.lastpoint, { x: x1, y: y1 }, { x: x2, y: y2 }, radius);
      pdfCtx.lineTo(plan.start.x, plan.start.y);
      pdfCtx.arc(plan.center.x, plan.center.y, plan.radius, plan.startAngle, plan.endAngle, plan.counterClockwise);
      this.lastpoint = plan.end;
    },

    lineTo(x: number, y: number) {
      pdfCtx.lineTo(x, y);
      this.lastpoint = { x, y };
    },

    bezierCurveTo(cp1x: number, cp1y: number, cp2x: number, cp2y: number, x: number, y: number) {
      pdfCtx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, x, y);
      this.lastpoint = { x, y };
    },

    quadraticCurveTo(cpx: number, cpy: number, x: number, y: number) {
      if (cpx == undefined || cpy == undefined || x == undefined || y == undefined) {
        // I've seen invalid lines in canvas: lines with points [0,0,0,0,0,0]
        // These lines are a single dot, and Konva gives 'undefined' for the points to this function
        // jspdf won't have any of this nonsense
        return;
      }
      pdfCtx.quadraticCurveTo(cpx, cpy, x, y);
      this.lastpoint = { x, y };
    },

    rect(x: number, y: number, width: number, height: number) {
      pdfCtx.rect(x, y, width, height);
    },

    roundRect(x: number, y: number, width: number, height: number, cornerRadius: number | number[]) {
      // this implementation follows the spec of Canvas, not the one of Konva
      let topleft = 0,
        topright = 0,
        bottomright = 0,
        bottomleft = 0;
      if (typeof cornerRadius == "number") {
        topleft = topright = bottomright = bottomleft = cornerRadius;
      } else if (Array.isArray(cornerRadius)) {
        switch (cornerRadius.length) {
          case 1: {
            topleft = topright = bottomright = bottomleft = cornerRadius[0];
            break;
          }
          case 2: {
            topleft = bottomright = cornerRadius[0];
            topright = bottomleft = cornerRadius[1];
            break;
          }
          case 3: {
            topleft = cornerRadius[0];
            topright = bottomleft = cornerRadius[1];
            bottomright = cornerRadius[2];
            break;
          }
          case 4: {
            [topleft, topright, bottomright, bottomleft] = cornerRadius;
            break;
          }
          default: {
            throw new Error("invalid radii array");
          }
        }
      }
      // TODO: what to do with DOMPointInit ?

      // handle negative width and height (flip left/right and top/bottom)
      if (width < 0) {
        // flip horizontal corners, make sure x is the left side, and width is positive
        x = Math.min(x, x + width);
        width = -width;
        [topleft, topright, bottomright, bottomleft] = [topright, topleft, bottomleft, bottomright];
      }
      if (height < 0) {
        // flip vertical corners, make sure y is the top side, and height is positive
        y = Math.min(y, y + height);
        height = -height;
        [topleft, topright, bottomright, bottomleft] = [bottomleft, bottomright, topright, topleft];
      }
      // scale down radius if the rect is too small
      const min = Math.min(width / 2, height / 2);
      topleft = Math.min(topleft, min);
      topright = Math.min(topright, min);
      bottomright = Math.min(bottomright, min);
      bottomleft = Math.min(bottomleft, min);

      pdfCtx.moveTo(x + topleft, y);
      pdfCtx.lineTo(x + width - topright, y);
      pdfCtx.arc(x + width - topright, y + topright, topright, -Math.PI / 2, 0, false);
      pdfCtx.lineTo(x + width, y + height - bottomright);
      pdfCtx.arc(x + width - bottomright, y + height - bottomright, bottomright, 0, Math.PI / 2, false);
      pdfCtx.lineTo(x + bottomleft, y + height);
      pdfCtx.arc(x + bottomleft, y + height - bottomleft, bottomleft, Math.PI / 2, Math.PI, false);
      pdfCtx.lineTo(x, y + topleft);
      pdfCtx.arc(x + topleft, y + topleft, topleft, Math.PI, -Math.PI / 2, false);
      pdfCtx.closePath();
    },

    drawImage(image: string, x: number, y: number, width: number, height: number) {
      pdfCtx.drawImage(image, x, y, width, height);
    },

    ellipse(
      _x: number,
      _y: number,
      _radiusX: number,
      _radiusY: number,
      _rotation: number,
      _startAngle: number,
      _endAngle: number,
      _counterclockwise?: boolean
    ) {
      throw new Error("don't use ellipse in pdf context");
    },
    //--------------------------------

    setLineDash(segments: number[]) {
      (pdfCtx as any).lineDash = segments; // jspdf has this, but not in the types
    },
    getLineDash() {
      return (pdfCtx as any).lineDash;
    },

    //--------------------------------

    stroke(_path2d?: Path2D) {
      pdfCtx.stroke();
    },
    strokeText(text: string, x: number, y: number) {
      pdfCtx.strokeText(text, x, y);
    },
    strokeRect(x: number, y: number, width: number, height: number) {
      pdfCtx.strokeRect(x, y, width, height);
    },

    //--------------------------------

    transform(a: number, b: number, c: number, d: number, e: number, f: number) {
      pdfCtx.transform(a, b, c, d, e, f);
    },
    translate(x: number, y: number) {
      pdfCtx.translate(x, y);
    },
    scale(x: number, y: number) {
      pdfCtx.scale(x, y);
    },
    rotate(angleRadians: number) {
      pdfCtx.rotate(angleRadians);
    },
    save() {
      pdfCtx.save();
    },
    restore() {
      pdfCtx.restore();
    },

    // -----------------------------
    _fillstring: "",
    get fillStyle() {
      return this._fillstring;
    },
    set fillStyle(value: string) {
      this._fillstring = value;
      const c = Color.create(value);
      if (c) {
        pdfCtx.fillStyle = c.color;
        doc.setGState(doc.GState({ opacity: c.alpha }));
      } else {
        pdfCtx.fillStyle = "transparent";
      }
    },

    _strokeStyle: "",
    get strokeStyle() {
      return this._strokeStyle;
    },
    set strokeStyle(value: string) {
      this._strokeStyle = value;
      const c = Color.create(value);
      if (c) {
        pdfCtx.strokeStyle = c.color;
        doc.setGState(doc.GState({ "stroke-opacity": c.alpha }));
      } else {
        pdfCtx.strokeStyle = "transparent";
      }
    },
  };
  fakeKonvaCtx._context = fakeKonvaCtx; // circular reference on purpose.

  const sharedProps = [
    "globalAlpha", // completely useless in js-pdf, don't use it. here for compatibility
    "globalCompositeOperation",
    "font",
    "textAlign",
    "textBaseline",
    "lineCap",
    "lineDashOffset",
    "lineJoin",
    "lineWidth",
    "miterLimit",
  ] as const;
  sharedProps.map((prop) =>
    Object.defineProperty(fakeKonvaCtx, prop, {
      get: () => pdfCtx[prop],
      set: (value: typeof pdfCtx[typeof prop]) => {
        (pdfCtx as any)[prop] = value;
      },
    })
  );

  return fakeKonvaCtx;
}

function svgArcToCanvasArc(
  x1: number,
  y1: number, // Starting point
  rx: number,
  ry: number, // X and Y radii
  xAxisRotation: number, // X-axis rotation in degrees
  largeArcFlag: number, // Large arc flag (0 or 1)
  sweepFlag: number, // Sweep flag (0 or 1)
  x2: number,
  y2: number, // Ending point
  ctx: any // Canvas context
): void {
  // Convert rotation to radians
  const rotationRad = (xAxisRotation * Math.PI) / 180;

  // Calculate the midpoint between start and end points
  const dx = (x1 - x2) / 2;
  const dy = (y1 - y2) / 2;

  // Apply rotation to the midpoint
  const x1p = Math.cos(rotationRad) * dx + Math.sin(rotationRad) * dy;
  const y1p = -Math.sin(rotationRad) * dx + Math.cos(rotationRad) * dy;

  // Ensure radii are large enough
  rx = Math.abs(rx);
  ry = Math.abs(ry);
  const lambda = (x1p * x1p) / (rx * rx) + (y1p * y1p) / (ry * ry);
  if (lambda > 1) {
    rx *= Math.sqrt(lambda);
    ry *= Math.sqrt(lambda);
  }

  // Calculate center point
  const sign = largeArcFlag === sweepFlag ? -1 : 1;
  const sq =
    (rx * rx * ry * ry - rx * rx * y1p * y1p - ry * ry * x1p * x1p) / (rx * rx * y1p * y1p + ry * ry * x1p * x1p);
  const coef = sign * Math.sqrt(Math.max(sq, 0));
  const cxp = coef * ((rx * y1p) / ry);
  const cyp = coef * (-(ry * x1p) / rx);

  // Calculate final center point
  const cx = Math.cos(rotationRad) * cxp - Math.sin(rotationRad) * cyp + (x1 + x2) / 2;
  const cy = Math.sin(rotationRad) * cxp + Math.cos(rotationRad) * cyp + (y1 + y2) / 2;

  // Calculate start and end angles
  const startAngle = Math.atan2((y1p - cyp) / ry, (x1p - cxp) / rx);
  const endAngle = Math.atan2((-y1p - cyp) / ry, (-x1p - cxp) / rx);

  // Draw the arc
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(rotationRad);
  ctx.scale(rx, ry);
  ctx.arc(0, 0, 1, startAngle, endAngle, sweepFlag === 0);
  ctx.restore();
}

// calculate point to give to doc.addImage, assuming p is the center point of the (rotated) image
// and (width,height) its size.
// Note: doc.addImage first places point at top-left, then rotates around bottom-left
function addImageCentered(px: number, py: number, width: number, height: number, A: Angle) {
  const vx = -width / 2;
  const vy = height / 2;

  const vrotX = vx * A.cos - vy * A.sin;
  const vrotY = vx * A.sin + vy * A.cos;

  const BLx = px + vrotX;
  const BLy = py + vrotY;

  const TLx = BLx;
  const TLy = BLy - height;
  return { x: TLx, y: TLy };
}

interface IAngle {
  readonly degrees: number;
  readonly radians: number;
  get cos(): number;
  get sin(): number;
}

class Angle implements IAngle {
  public readonly degrees: number;
  public readonly radians: number;

  private constructor(d: number) {
    this.degrees = d;
    this.radians = (d * Math.PI) / 180;
  }

  public static fromDegrees(x: number) {
    return x == 0 ? Angle.Zero : new Angle(x);
  }

  public static fromRadians(x: number) {
    return new Angle((x * 180) / Math.PI);
  }

  public get sin() {
    return Math.sin(this.radians);
  }

  public get cos() {
    return Math.cos(this.radians);
  }

  static readonly Zero: IAngle = Object.freeze({ degrees: 0, radians: 0, cos: 1, sin: 0 });
}
